import {ClassBoardResources} from './ClassBoardResources';
import {attemptSessionId} from "./activity-continuity";
import {freeAccess} from "./class-assignments";
import {studentBinder, studentDailyWork, studentWorkStatus} from "./student-work-view";
import { useEffect, useState } from "react";
import { libraryDemo, useActivityLibrary } from "./activity-library-client";
import {
  versionFor,
  attemptAccess,
  ownsAttempt,
  type StudentRef,
  type StudentAttempt,
} from "./activity-library";
import { LibraryWork } from "./LibraryWork";
import "./activity-library-v2.css";
import {BinderCopyActions} from "./BinderCopyActions";

export function StudentLibrary({
  student,
  initialTab = "todo",
  hideNavigation = false,
}: {
  student?: StudentRef;
  initialTab?: "todo" | "progress" | "completed" | "binder" | "competencies";
  hideNavigation?: boolean;
}) {
  const [tab, setTab] = useState(initialTab);
  const [work, setWork] = useState<string | null>(null);
  const [sessionOpened,setSessionOpened]=useState(false);
  const { data, error, busy, execute, refresh } = useActivityLibrary(student, work);
  useEffect(() => {
    if (work) return; // Never replace an open answer form with a background refresh.
    const update = () => { if (document.visibilityState === "visible") void refresh(); };
    const timer = window.setInterval(update, 15000);
    window.addEventListener("focus", update);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", update); };
  }, [work, refresh]);
  useEffect(()=>{
    const sessionId=new URLSearchParams(location.search).get('session');
    if(!sessionId||!data||sessionOpened)return;
    if(work){setSessionOpened(true);return;}
    const a=data.state.attempts.find(a=>attemptSessionId(a)===sessionId&&(!student||ownsAttempt(a,student)));
    if(a&&a.status!=='not_started'){setSessionOpened(true);setWork(a.id);}
  },[data,work,sessionOpened,student?.classId,student?.studentId]);
  useEffect(()=>{
    const pdfAttemptId=new URLSearchParams(location.search).get("pdfAttempt");
    if(!pdfAttemptId||!data||work)return;
    const copy=data.state.attempts.find(a=>a.id===pdfAttemptId&&a.restitution?.status==="released"&&a.copyAccessible!==false);
    if(copy)setWork(copy.id);
  },[data,work]);
  if (!data)
    return (
      <section className="library-page">
        <p role="alert">{error || "Chargement du classeur…"}</p>
        <button onClick={() => void refresh()}>Réessayer</button>
      </section>
    );
  const { state } = data;
  const isVisible=(a: typeof state.assignments[number])=>freeAccess(state,a,student?.classId??a.recipients[0]?.classId??"");
  const accessOpen=(a: StudentAttempt)=>!!attemptSessionId(a) || !a.assignmentId || state.assignments.some(row=>row.id===a.assignmentId&&isVisible(row));
  const requestedSessionId=new URLSearchParams(location.search).get("session");
  const daily = studentDailyWork(state).filter(a=>!requestedSessionId||attemptSessionId(a)===requestedSessionId);
  const binder = studentBinder(state);
  const attempt = state.attempts.find((a) => a.id === work);
  if (attempt)
    return (
      <section className="library-page">
        {error && <p role="alert">{error}</p>}
        <LibraryWork
          key={attempt.id}
          version={versionFor(state, attempt)}
          attempt={attempt}
          readOnly={(data.role === "teacher" && !libraryDemo) || !accessOpen(attempt)}
          access={attemptAccess(state, attempt)}
          activeUntil={state.digitalSessions?.find(d => d.id === attemptSessionId(attempt))?.activeUntil}
          busy={busy}
          execute={execute}
          onRefresh={refresh}
          onBack={() => setWork(null)}
        />
      </section>
    );
  return (
    <section className="library-page">
      {!hideNavigation && (
        <nav
          className="library-tabs"
          aria-label="Travail et classeur de l’élève"
        >
          {(
            ["todo", "binder", "competencies"] as const
          ).map((t) => (
            <button
              aria-current={tab === t ? "page" : undefined}
              key={t}
              onClick={() => setTab(t)}
            >
              {
                {
                  todo: "À faire",
                  progress: "En cours",
                  completed: "Terminées",
                  binder: "Classeur",
                  competencies: "Compétences",
                }[t]
              }
            </button>
          ))}
        </nav>
      )}
      {error && <p role="alert">{error}</p>}
      {(tab === "todo" || tab === "progress") && (
        <div className="library-cards">
          {daily.map(a => {
            const v = versionFor(state, a);
            const canStart = a.status === "not_started";
            return <article key={a.id}>
              <h3>{v.title}</h3>
              <p>{v.chapterTitle}</p>
              <p><strong>{a.status === "completed" && !a.restitution ? "Corrigé — en attente de restitution" : studentWorkStatus(a.status)}</strong>{a.ownerType === "group" && ` · ${a.groupName}`}</p>
              <button disabled={busy || (canStart && data.role === "teacher" && !libraryDemo)} onClick={async () => {
                if (!canStart) { setWork(a.id); return; }
                const assignment = state.assignments.find(row => row.id === a.assignmentId);
                if (!assignment) return;
                try {
                  const result = await execute({type: "start", assignmentId: assignment.id, student: student ?? assignment.recipients[0]});
                  const started = result.state.attempts.find(t => t.assignmentId === assignment.id && ownsAttempt(t, student ?? assignment.recipients[0]));
                  if (started) setWork(started.id);
                } catch { /* The hook displays the error. */ }
              }}>{a.copyAccessible === false ? "Voir les compétences" : canStart ? "Commencer" : a.status === "in_progress" ? "Continuer" : "Consulter"}</button>
            </article>;
          })}
          {!daily.length && <p>Aucune activité en cours de séance ou en attente de correction. Les autres travaux restent dans le classeur.</p>}
        </div>
      )}
      {(tab === "binder" || tab === "completed") && (
        <div className="student-binder">
          <h2>Classeur</h2><ClassBoardResources classId={student?.classId}/>
          {!binder.size && <p>Les copies restituées par le professeur apparaîtront ici.</p>}
          {[...binder].map(([themeId, theme]) => <section key={themeId}>
            <h3>{theme.title}</h3>
            {[...theme.chapters].map(([chapterId, chapter]) => <details open key={chapterId}>
              <summary>{chapter.title}</summary>
              <ul>{chapter.attempts.map(a => <li key={a.id}>
                <span>{versionFor(state, a).title}</span>
                <small>{studentWorkStatus(a.status)}</small>
                <BinderCopyActions
                  title={versionFor(state, a).title}
                  onConsult={() => setWork(a.id)}
                  pdfHref={`/student-copy/pdf?attemptId=${encodeURIComponent(a.id)}`}
                />
              </li>)}</ul>
            </details>)}
          </section>)}
        </div>
      )}
      {tab === "competencies" && (
        <>
          <h2>Évolution des compétences</h2>
          <p>
            Le niveau actuel est la dernière validation A, B, C ou D. Les
            absences et les mentions « Non évalué » sont conservées sans
            remplacer ce niveau.
          </p>
          {!state.history.length && (
            <p>Aucune validation finalisée dans la bibliothèque.</p>
          )}
          {[...new Set(state.history.map((h) => h.competencyId))].map((id) => {
            const history = state.history
              .filter((h) => h.competencyId === id)
              .sort(
                (a, b) =>
                  a.validatedAt.localeCompare(b.validatedAt) ||
                  a.id.localeCompare(b.id),
              );
            const latest = history.at(-1)!;
            const current = history
              .filter((h) => ["A", "B", "C", "D"].includes(h.level))
              .at(-1);
            return (
              <article className="library-history" key={id}>
                <h3>
                  {latest.competenceCode} — {latest.competenceLabel}
                </h3>
                <p>
                  Niveau actuel :{" "}
                  <strong>{current?.level ?? "Non évalué"}</strong> · Dernière
                  observation :{" "}
                  {new Date(latest.validatedAt).toLocaleDateString("fr-FR")}
                </p>
                <ol>
                  {history.map((h) => (
                    <li key={h.id}>
                      <time>
                        {new Date(h.validatedAt).toLocaleDateString("fr-FR")}
                      </time>{" "}
                      →{" "}
                      <strong>
                        {h.level === "ABS"
                          ? "Absent"
                          : h.level === "NE"
                            ? "Non évalué"
                            : h.level}
                      </strong>{" "}
                      ·{" "}
                      {h.source === "legacy_session" ? (
                        <span>
                          {h.activityTitle} · séance historique (énoncé non
                          archivé)
                        </span>
                      ) : (
                        <button onClick={() => setWork(h.attemptId)}>
                          {h.activityTitle} · v{h.activityVersion}
                        </button>
                      )}
                      {h.comment && <p>{h.comment}</p>}
                    </li>
                  ))}
                </ol>
              </article>
            );
          })}
        </>
      )}
    </section>
  );
}
